<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <div class="header offers-header">
            <div>
              <h2>Offers List</h2>
              <p class="text-muted mb-0">
                Manage offer creatives, business campaigns and multi-country visibility.
              </p>
            </div>
            <template v-if="$store.getters['authpermission/hasAnyPermission'](['business.status'])">
              <b-button variant="primary" class="btn-mw" @click="$router.push('/offers/add')">
                <i class="fa fa-plus mr-1"></i>
                Create Offer
              </b-button>
            </template>
          </div>

          <div class="body pt-0">
            <div class="offers-toolbar">
              <div class="offers-toolbar__summary">
                <div class="summary-pill">
                  <span class="summary-pill__label">Total Offers</span>
                  <strong>{{ totalRows }}</strong>
                </div>
                <div class="summary-pill" v-if="hasActiveFilters">
                  <span class="summary-pill__label">Active Filters</span>
                  <strong>{{ activeFilterCount }}</strong>
                </div>
              </div>

              <div class="offers-filters">
                <div class="offers-filter-field">
                  <label class="offers-filter-label">Offer Type</label>
                  <div class="filter-input-wrapper">
                    <i class="fa fa-tags filter-input-icon"></i>
                    <input
                      v-model.trim="filters.offer_type"
                      type="text"
                      class="form-control filter-input"
                      placeholder="Search by offer type"
                      @keyup.enter="applyFilters"
                    />
                  </div>
                </div>

                <div class="offers-filter-field">
                  <label class="offers-filter-label">Business</label>
                  <div class="filter-input-wrapper">
                    <i class="fa fa-briefcase filter-input-icon"></i>
                    <input
                      v-model.trim="filters.business_name"
                      type="text"
                      class="form-control filter-input"
                      placeholder="Search by business name"
                      @keyup.enter="applyFilters"
                    />
                  </div>
                </div>

                <div class="offers-filter-actions">
                  <b-button variant="primary" class="btn-mw" @click="applyFilters">
                    <i class="fa fa-filter mr-1"></i>
                    Apply
                  </b-button>
                  <b-button variant="outline-secondary" class="btn-mw" @click="resetFilters">
                    Reset
                  </b-button>
                </div>
              </div>
            </div>

            <div class="offers-table-card">
              <div class="offers-table-meta">
                <div>
                  <h5 class="mb-1">Offer Directory</h5>
                  <small class="text-muted">{{ paginationSummary }}</small>
                </div>
                <div class="offers-sort-hint text-muted">
                  <i class="fa fa-exchange mr-1"></i>
                  Click column headers to sort
                </div>
              </div>

              <b-table
                responsive
                table-class="mb-0 table-custom border offers-table"
                head-variant="light"
                :items="items"
                :fields="fields"
                :per-page="perPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                @sort-changed="onSortChanged"
                striped
                show-empty
              >
                <template #head()="{ label, field: { key } }">
                  {{ label }}
                  <template>
                    <i v-if="sortBy !== key"></i>
                    <i v-else-if="sortDesc" class="fa fa-long-arrow-down ml-1"></i>
                    <i v-else class="fa fa-long-arrow-up ml-1"></i>
                  </template>
                </template>

                <template #cell(business_name)="row">
                  <div class="offer-text-cell">
                    <strong>{{ row.value || "Unknown business" }}</strong>
                  </div>
                </template>

                <template #cell(country_names_display)="row">
                  <div v-if="row.item.country_names && row.item.country_names.length" class="offer-country-list">
                    <span v-for="countryName in row.item.country_names" :key="`${row.item.id}-${countryName}`" class="offer-country-chip">
                      {{ countryName }}
                    </span>
                  </div>
                  <span v-else class="offer-country-chip">-</span>
                </template>

                <template #cell(offer_type)="row">
                  <span class="offer-type-chip">{{ row.value }}</span>
                </template>

                <template #cell(offer_image)="row">
                  <img
                    v-if="row.value"
                    :src="row.value"
                    alt="Offer"
                    class="offer-image-preview"
                    @error="onImageError"
                  />
                  <div v-else class="offer-image-placeholder">No image</div>
                </template>

                <template #cell(status)="row">
                  <span class="offer-status-badge" :class="statusClass(row.value)">
                    {{ row.value }}
                  </span>
                </template>

                <template #cell(action)="row">
                  <b-dropdown text="Actions" right variant="outline-primary" size="sm" class="offers-actions">
                    <b-dropdown-item @click="$router.push(`/offers/details/${row.item.id}`)">
                      View Details
                    </b-dropdown-item>
                    <template v-if="$store.getters['authpermission/hasAnyPermission'](['business.status'])">
                      <b-dropdown-item @click="$router.push(`/offers/edit/${row.item.id}`)">
                        Edit
                      </b-dropdown-item>
                    </template>
                    <template v-if="$store.getters['authpermission/hasAnyPermission'](['business.delete'])">
                      <b-dropdown-item @click="deleteOffer(row.item.id)">Delete</b-dropdown-item>
                    </template>
                  </b-dropdown>
                </template>

                <template #empty>
                  <div class="offers-empty-state">
                    <div class="offers-empty-state__icon">
                      <i class="fa fa-tags"></i>
                    </div>
                    <h5>No offers found</h5>
                    <p class="text-muted mb-3">
                      {{
                        hasActiveFilters
                          ? "Try adjusting your filters to see matching offers."
                          : "Create your first offer to start managing campaigns here."
                      }}
                    </p>
                    <div class="offers-empty-state__actions">
                      <b-button v-if="hasActiveFilters" variant="outline-secondary" @click="resetFilters">
                        Clear Filters
                      </b-button>
                      <b-button
                        v-if="$store.getters['authpermission/hasAnyPermission'](['business.status'])"
                        variant="primary"
                        @click="$router.push('/offers/add')"
                      >
                        <i class="fa fa-plus mr-1"></i>
                        Create Offer
                      </b-button>
                    </div>
                  </div>
                </template>
              </b-table>

              <div class="d-flex py-3 align-items-center justify-content-between table-pagination offers-pagination">
                <div class="per-page d-flex align-items-center">
                  <span class="mr-2 text-muted offers-pagination__label">Per page</span>
                  <b-form-select
                    id="offers-per-page"
                    v-model="perPage"
                    :options="pageOptions"
                    size="sm"
                    class="offers-per-page-select"
                  ></b-form-select>
                </div>
                <div class="number-page">
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    first-text="First"
                    last-text="Last"
                    :per-page="perPage"
                    align="right"
                  ></b-pagination>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <main-loader :loading="isLoading"></main-loader>
  </div>
</template>

<script>
import "@/plugins/sweetalert2";
import { GET_OFFERS_LIST, DELETE_OFFER } from "@/store/actions.type";

const LoaderComponent = require("@/components/admin/loader/LoaderComponent.vue").default;
const BreadCrumb = require("@/components/BreadCrumb.vue").default;

export default {
  name: "OffersListComponent",
  components: {
    "main-loader": LoaderComponent,
    "bread-crumb": BreadCrumb,
  },
  computed: {
    hasActiveFilters() {
      return !!(this.filters.business_name || this.filters.offer_type);
    },
    activeFilterCount() {
      return [this.filters.business_name, this.filters.offer_type].filter(Boolean).length;
    },
    paginationSummary() {
      if (!this.totalRows) return "No offers available";
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalRows);
      return `Showing ${start}-${end} of ${this.totalRows} offers`;
    },
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Offers", links: "" },
        { title: "Offers List", links: "" },
      ],
      fields: [
        { key: "business_name", label: "Business", sortable: true },
        { key: "country_names_display", label: "Countries", sortable: true },
        { key: "offer_type", label: "Offer Type", sortable: true },
        { key: "offer_image", label: "Image" },
        { key: "status", label: "Status", sortable: true },
        { key: "action", label: "Action", thClass: "text-right", tdClass: "text-right" },
      ],
      items: [],
      isLoading: false,
      currentPage: 1,
      perPage: 20,
      pageOptions: [10, 20, 50, 100],
      totalRows: 0,
      sortBy: "business_name",
      sortDesc: true,
      filters: {
        business_name: "",
        offer_type: "",
      },
    };
  },
  watch: {
    currentPage() {
      this.fetchOffers();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchOffers();
    },
  },
  mounted() {
    this.fetchOffers();
  },
  methods: {
    toApiSortBy(sortBy) {
      const map = {
        business_name: "business_name",
        country_names_display: "country_name",
        offer_type: "offer_type",
        status: "status",
      };
      return map[sortBy] || "created_at";
    },
    statusClass(status) {
      return {
        "is-active": status === "active",
        "is-inactive": status === "inactive",
        "is-expired": status === "expired",
      };
    },
    async fetchOffers() {
      this.isLoading = true;
      try {
        const response = await this.$store.dispatch(GET_OFFERS_LIST, {
          page: this.currentPage,
          limit: this.perPage,
          sort: this.sortDesc ? "desc" : "asc",
          sortby: this.toApiSortBy(this.sortBy),
          ...(this.filters.business_name ? { business_name: this.filters.business_name } : {}),
          ...(this.filters.offer_type ? { offer_type: this.filters.offer_type } : {}),
        });

        const details = response?.data?.details || [];
        this.items = details.map((item) => ({
          id: item.id,
          business_id: item.business_id,
          business_name: item.business_name,
          country_names: Array.isArray(item.country_names)
            ? item.country_names
            : item.country_name
              ? item.country_name.split(", ").filter(Boolean)
              : [],
          country_names_display: Array.isArray(item.country_names)
            ? item.country_names.join(", ")
            : item.country_name || "",
          offer_type: item.offer_type,
          offer_image: item.offer_image,
          status: item.status,
        }));
        this.totalRows = response?.data?.query?.total || 0;
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Failed to load offers.", {
          position: "top-right",
        });
        this.items = [];
        this.totalRows = 0;
      } finally {
        this.isLoading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchOffers();
    },
    resetFilters() {
      this.filters.business_name = "";
      this.filters.offer_type = "";
      this.currentPage = 1;
      this.fetchOffers();
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.fetchOffers();
    },
    async deleteOffer(id) {
      const result = await this.$swal.fire({
        title: "Are you sure?",
        text: "You want to delete this offer.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
      });

      if (!result.isConfirmed) return;

      this.isLoading = true;
      try {
        const response = await this.$store.dispatch(DELETE_OFFER, id);
        const isSuccessWithBody = !!response?.data?.code;
        if (isSuccessWithBody) {
          this.$toast.success(response?.data?.message || "Offer deleted successfully.", {
            position: "top-right",
          });
          this.fetchOffers();
        } else {
          this.$toast.error("Failed to delete offer.", { position: "top-right" });
        }
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Failed to delete offer.", {
          position: "top-right",
        });
      } finally {
        this.isLoading = false;
      }
    },
    onImageError(event) {
      event.target.style.display = "none";
    },
  },
};
</script>

<style scoped>
.offers-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.offers-header p {
  margin-top: 4px;
}

.offers-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 0 18px;
}

.offers-toolbar__summary {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.summary-pill {
  min-width: 130px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #edf2f7;
}

.summary-pill__label {
  display: block;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 3px;
}

.summary-pill strong {
  color: #1a2343;
  font-size: 20px;
  line-height: 1;
}

.offers-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
  flex: 1;
  justify-content: flex-end;
}

.offers-filter-field {
  min-width: 230px;
}

.offers-filter-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #1a2343;
}

.filter-input-wrapper {
  position: relative;
}

.filter-input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9aa5b1;
  z-index: 2;
}

.filter-input {
  padding-left: 40px;
  min-height: 44px;
}

.offers-filter-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.offers-filter-actions .btn {
  min-height: 44px;
}

.offers-table-card {
  border: 1px solid #e9ecef;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(26, 35, 67, 0.05);
}

.offers-table-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #edf2f7;
  background: linear-gradient(180deg, #fcfdff 0%, #f8fafc 100%);
}

.offers-sort-hint {
  font-size: 13px;
}

.offers-table :deep(thead th) {
  white-space: nowrap;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5f6b7a;
  background: #f8fafc;
  border-top: 0;
}

.offers-table :deep(tbody td) {
  vertical-align: middle;
  padding-top: 14px;
  padding-bottom: 14px;
}

.offer-text-cell {
  display: flex;
  flex-direction: column;
}

.offer-text-cell strong {
  color: #1a2343;
  font-size: 13px;
}

.offer-text-cell small {
  color: #6c757d;
  font-size: 11px;
  margin-top: 2px;
  word-break: break-all;
}

.offer-country-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f8fafc;
  color: #445063;
  font-weight: 600;
  font-size: 12px;
  border: 1px solid #e6edf5;
}

.offer-country-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.offer-type-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(71, 167, 184, 0.12);
  color: #157786;
  font-weight: 600;
  font-size: 12px;
}

.offer-image-preview {
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
  background: #fff;
  padding: 8px;
}

.offer-image-placeholder {
  width: 72px;
  height: 72px;
  border-radius: 10px;
  border: 1px dashed #ced4da;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #6c757d;
  background: #f8fafc;
}

.offer-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.offer-status-badge.is-active {
  background: rgba(111, 183, 63, 0.12);
  color: #4c8e22;
}

.offer-status-badge.is-inactive {
  background: rgba(108, 117, 125, 0.12);
  color: #5f6b7a;
}

.offer-status-badge.is-expired {
  background: rgba(216, 66, 77, 0.12);
  color: #b2343d;
}

.offers-actions :deep(.btn) {
  min-width: 96px;
  min-height: 36px;
}

.offers-empty-state {
  padding: 48px 20px;
  text-align: center;
}

.offers-empty-state__icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: rgba(71, 167, 184, 0.12);
  color: #1f8d9a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.offers-empty-state h5 {
  color: #1a2343;
  margin-bottom: 8px;
}

.offers-empty-state__actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.offers-pagination {
  padding: 16px 20px !important;
  border-top: 1px solid #edf2f7;
  background: #fff;
}

.offers-pagination__label {
  font-size: 13px;
}

.offers-per-page-select {
  min-width: 86px;
}

@media (max-width: 991px) {
  .offers-header,
  .offers-table-meta {
    flex-direction: column;
    align-items: flex-start;
  }

  .offers-filters {
    justify-content: flex-start;
  }
}

@media (max-width: 767px) {
  .offers-toolbar,
  .offers-pagination {
    gap: 14px;
  }

  .offers-filter-field {
    min-width: 100%;
  }

  .offers-filter-actions {
    width: 100%;
  }

  .offers-filter-actions .btn {
    flex: 1;
  }

  .offers-pagination {
    flex-direction: column;
    align-items: flex-start !important;
  }
}
</style>
